import React, { useEffect, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'
import { FieldErrors } from 'react-hook-form/dist/types/errors'
import { TextField } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { Paragraph, Text } from 'components/shared'
import { getValidationMessage } from 'utils'

export interface ReactHookInputType {
  defaultValue?: unknown
  label: string
  placeholder?: string
  name: string
  type?: string
  disabled?: boolean
  multiline?: boolean
  maxLenCounter?: number
  control: any
  rules?: RegisterOptions
  errors: FieldErrors
  id?: string
}

interface IUseStyles {
  startAdornmentWidth?: number
}

export const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      padding: '20px 0 0',

      '& .MuiInputBase-root': {
        marginTop: 0,
        position: 'relative',
      },
      '& .MuiInputBase-input': {
        borderRadius: 30,
        height: 'auto',
        backgroundColor: theme.colors.greySemiLight,
        padding: '9px 16px',

        [theme.breakpoints.up(1024)]: {
          padding: '14px 24px 15px',
        },

        [theme.breakpoints.down(1023)]: {
          fontSize: 14,
        },

        '&.MuiInputBase-inputAdornedEnd': {
          paddingRight: 40,
        },

        '&::placeholder': {
          opacity: 1,
          color: theme.colors.greySemiDark,
        },

        '&[readonly]': {
          color: theme.colors.greySemiDark,
        },
      },
      '& textarea.MuiInputBase-input': {
        borderRadius: 16,
      },
      '& .MuiInputLabel-root': {
        zIndex: 1,
        transform: 'translate(16px, 32px)',
        maxWidth: ({ startAdornmentWidth = 0 }: IUseStyles) => `calc(100% - ${startAdornmentWidth + 16}px)`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        color: theme.colors.greySemiDark,

        [theme.breakpoints.up(1024)]: {
          maxWidth: ({ startAdornmentWidth = 0 }: IUseStyles) => `calc(100% - ${startAdornmentWidth + 24}px)`,
          transform: 'translate(24px, 35px)',
        },

        [theme.breakpoints.down(1023)]: {
          fontSize: 12,
        },
      },
      '& .MuiInputLabel-shrink': {
        zIndex: 1,
        transform: 'translate(16px, 6px)!important',
        fontSize: 12,

        [theme.breakpoints.up(1024)]: {
          transform: 'translate(24px, 0)!important',
        },
      },
      '& .MuiFormHelperText-root': {
        minHeight: 20,
        fontFamily: theme.fonts.inter,
        color: theme.colors.red,
        letterSpacing: 'normal',
        fontSize: 12,
        lineHeight: 1.5,
        maxWidth: '100%',
        paddingLeft: 16,
        marginTop: 0,

        [theme.breakpoints.up(1024)]: {
          paddingLeft: 24,
        },
      },
      '& .MuiInputAdornment-root': {
        position: 'absolute',
        right: 14,
        top: '50%',
        width: 24,
        height: 24,
        cursor: 'pointer',
        transform: 'translate(0, -50%)',
        fill: theme.colors.black,
      },
      '& .counter': {
        position: 'absolute',
        top: -24,
        right: 0,
        paddingLeft: 16,
        '& span': {
          color: theme.colors.greySemiDark,
        },
      },
    },
    inputRoot: {
      padding: 0,
    },
  })
)

const ReactHookInput = (props: ReactHookInputType) => {
  const {
    name,
    label,
    defaultValue,
    control,
    rules,
    errors,
    type = 'text',
    multiline,
    maxLenCounter = 0,
    disabled = false,
    placeholder,
    id,
  } = props
  const startAdornmentRef = useRef<HTMLDivElement>(null)
  const [startAdornmentWidth, setStartAdornmentWidth] = useState(0)
  const classes = useStyles({ startAdornmentWidth })
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    setStartAdornmentWidth(startAdornmentRef?.current?.clientWidth || 0)
  }, [startAdornmentRef?.current?.clientWidth])

  return (
    <Controller
      render={({ field: { onChange, value, name, ref } }) => (
        <TextField
          data-id={id}
          fullWidth
          InputProps={{
            classes: {
              root: classes.inputRoot,
            },
            startAdornment: maxLenCounter ? (
              <div ref={startAdornmentRef} className="counter">
                <Paragraph size={'sm'}>
                  {value.length} <Text size={'sm'}>/ {maxLenCounter}</Text>
                </Paragraph>
              </div>
            ) : null,
            readOnly: disabled,
            endAdornment:
              type === 'password' ? (
                <InputAdornment position="end" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <svg width="24px" height="24px">
                      <use xlinkHref="#eye-closed" />
                    </svg>
                  ) : (
                    <svg width="24px" height="24px">
                      <use xlinkHref="#eye" />
                    </svg>
                  )}
                </InputAdornment>
              ) : null,
          }}
          InputLabelProps={placeholder ? { shrink: true } : {}}
          classes={{
            root: classes.root,
          }}
          rows={5}
          onChange={onChange}
          inputRef={ref}
          label={label}
          placeholder={placeholder}
          multiline={multiline}
          type={type === 'password' && showPassword ? 'text' : type}
          id={name}
          value={value}
          defaultValue={defaultValue}
          helperText={<span>{getValidationMessage(name, errors)}</span>}
        />
      )}
      name={name}
      control={control}
      rules={rules}
    />
  )
}

export default ReactHookInput
